/**
 * Qualification Data Contract — strict frontend types based on API.
 *
 * All field names match the API exactly. No renaming. No inferred fields.
 * Explicit handling for null, undefined, and empty values.
 */

// ─── Constants (matching backend enums) ──────────────────────────

export const VALID_OPERATORS = ['equals', 'contains', 'greater_than', 'less_than'];
export const VALID_VALUE_TYPES = ['string', 'number', 'boolean'];
export const VALID_SIGNAL_TYPES = ['budget', 'authority', 'need', 'timeline', 'intent', 'pain_point', 'custom'];
export const VALID_POLICIES = ['strict', 'high_confidence_ok', 'never_required'];
export const VALID_LABELS = ['hot', 'warm', 'cold', 'disqualified'];
export const VALID_Q_STATES = ['in_progress', 'needs_review', 'finalized'];

export const OPERATOR_TYPE_COMPAT = {
  equals: ['string', 'number', 'boolean'],
  contains: ['string'],
  greater_than: ['number'],
  less_than: ['number'],
};

export const OPERATOR_LABELS = {
  equals: 'Equals',
  contains: 'Contains',
  greater_than: 'Greater than',
  less_than: 'Less than',
};

export const SIGNAL_TYPE_LABELS = {
  budget: 'Budget',
  authority: 'Authority',
  need: 'Need',
  timeline: 'Timeline',
  intent: 'Intent',
  pain_point: 'Pain Point',
  custom: 'Custom',
};

export const POLICY_LABELS = {
  strict: 'Strict — only direct answers count',
  high_confidence_ok: 'High Confidence OK — inferred high-confidence accepted',
  never_required: 'Never Required — all terminal states accepted',
};

export const LABEL_CONFIG = {
  hot: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', ring: 'ring-red-300' },
  warm: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', ring: 'ring-amber-300' },
  cold: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', ring: 'ring-blue-300' },
  disqualified: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300', ring: 'ring-gray-300' },
};

export const STATE_CONFIG = {
  finalized: { bg: 'bg-green-50', text: 'text-green-700', label: 'Finalized' },
  needs_review: { bg: 'bg-yellow-50', text: 'text-yellow-700', label: 'Needs Review' },
  in_progress: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'In Progress' },
};


// ─── Schema Validation (lightweight guard) ───────────────────────

/**
 * Validate a qualification API response.
 * Returns { valid: true } or { valid: false, errors: string[] }.
 */
export function validateQualificationResponse(data) {
  const errors = [];

  if (data === null || data === undefined || typeof data !== 'object') {
    return { valid: false, errors: ['Response is not an object'] };
  }

  // Required top-level fields
  const requiredStr = ['session_id', 'qr_id'];
  for (const f of requiredStr) {
    if (typeof data[f] !== 'string') {
      errors.push(`Missing or invalid field: ${f}`);
    }
  }

  if (typeof data.qualification_available !== 'boolean') {
    errors.push('Missing or invalid field: qualification_available');
  }

  // If qualification is available, validate result fields
  if (data.qualification_available === true) {
    if (typeof data.qualification_score !== 'number') {
      errors.push('Missing or invalid field: qualification_score');
    }
    if (typeof data.qualification_label !== 'string') {
      errors.push('Missing or invalid field: qualification_label');
    }
    if (typeof data.qualification_state !== 'string') {
      errors.push('Missing or invalid field: qualification_state');
    }
    if (!Array.isArray(data.qualification_reasons)) {
      errors.push('Missing or invalid field: qualification_reasons');
    }
    if (!Array.isArray(data.audit_events)) {
      errors.push('Missing or invalid field: audit_events');
    }
  }

  // insights field must always be present (real or fallback)
  if (!data.insights || typeof data.insights !== 'object') {
    errors.push('Missing or invalid field: insights');
  } else {
    const ins = data.insights;
    if (typeof ins.summary !== 'string') errors.push('insights.summary must be a string');
    if (typeof ins.confidence !== 'number') errors.push('insights.confidence must be a number');
    if (!Array.isArray(ins.evidence)) errors.push('insights.evidence must be an array');
  }

  return errors.length > 0 ? { valid: false, errors } : { valid: true };
}


// ─── Scoring Config Validation (mirrors backend) ────────────────

/**
 * Validate scoring config before submission.
 * Returns { blocking: string[], warnings: string[] }.
 */
export function validateScoringConfig(config) {
  const blocking = [];
  const warnings = [];

  if (config.hot_threshold <= config.warm_threshold) {
    blocking.push(`Hot threshold (${config.hot_threshold}) must be greater than warm threshold (${config.warm_threshold})`);
  }
  if (config.min_score >= config.max_score) {
    blocking.push(`Min score (${config.min_score}) must be less than max score (${config.max_score})`);
  }
  if (!VALID_POLICIES.includes(config.confirmation_policy)) {
    blocking.push(`Invalid confirmation policy: ${config.confirmation_policy}`);
  }
  if ((config.rules || []).length > 500) {
    blocking.push(`Too many rules (${config.rules.length}), maximum is 500`);
  }

  for (const rule of config.rules || []) {
    const rid = rule.id || '?';
    if (!rule.field_id) {
      blocking.push(`Rule [${rid}]: field_id is required`);
    }
    if (!VALID_OPERATORS.includes(rule.operator)) {
      blocking.push(`Rule [${rid}]: invalid operator "${rule.operator}"`);
    }
    if (!VALID_VALUE_TYPES.includes(rule.value_type)) {
      blocking.push(`Rule [${rid}]: invalid value_type "${rule.value_type}"`);
    } else if (OPERATOR_TYPE_COMPAT[rule.operator] && !OPERATOR_TYPE_COMPAT[rule.operator].includes(rule.value_type)) {
      blocking.push(`Rule [${rid}]: operator "${rule.operator}" is incompatible with value_type "${rule.value_type}"`);
    }
    if (!VALID_SIGNAL_TYPES.includes(rule.signal_type)) {
      blocking.push(`Rule [${rid}]: invalid signal_type "${rule.signal_type}"`);
    }
    if (rule.value_type === 'number' && rule.value !== '' && isNaN(Number(rule.value))) {
      blocking.push(`Rule [${rid}]: value "${rule.value}" is not a valid number`);
    }
    if (rule.confirmation_policy_override !== null &&
        rule.confirmation_policy_override !== undefined &&
        rule.confirmation_policy_override !== '' &&
        !VALID_POLICIES.includes(rule.confirmation_policy_override)) {
      blocking.push(`Rule [${rid}]: invalid confirmation_policy_override "${rule.confirmation_policy_override}"`);
    }
    if (rule.hard_disqualify && rule.points > 0) {
      warnings.push(`Rule [${rid}]: hard_disqualify is true but points > 0; points will be ignored when disqualified`);
    }
    if (rule.expires_after_seconds !== null && rule.expires_after_seconds !== undefined && rule.expires_after_seconds !== '') {
      const secs = Number(rule.expires_after_seconds);
      if (isNaN(secs) || secs <= 0) {
        blocking.push(`Rule [${rid}]: expires_after_seconds must be a positive number`);
      }
    }
  }

  for (const cr of config.conflict_rules || []) {
    const crid = cr.id || '?';
    if (cr.action !== 'cap_label') {
      blocking.push(`Conflict rule [${crid}]: action must be "cap_label"`);
    }
    if (!VALID_LABELS.includes(cr.cap_value)) {
      blocking.push(`Conflict rule [${crid}]: invalid cap_value "${cr.cap_value}"`);
    }
    if (!Array.isArray(cr.conditions) || cr.conditions.length === 0) {
      blocking.push(`Conflict rule [${crid}]: must have at least one condition`);
    }
    for (const cond of cr.conditions || []) {
      if (!cond.field_id) {
        blocking.push(`Conflict rule [${crid}]: condition field_id is required`);
      }
      if (!VALID_OPERATORS.includes(cond.operator)) {
        blocking.push(`Conflict rule [${crid}]: condition operator "${cond.operator}" is invalid`);
      }
      if (!VALID_VALUE_TYPES.includes(cond.value_type)) {
        blocking.push(`Conflict rule [${crid}]: condition value_type "${cond.value_type}" is invalid`);
      }
    }
  }

  return { blocking, warnings };
}


// ─── Request Dedup + Cache ───────────────────────────────────────

const _inflight = new Map();
const _cache = new Map();

/**
 * Build cache key for qualification data.
 * @param {string} sessionId
 * @param {number|null} configVersion
 */
export function cacheKey(sessionId, configVersion) {
  return `${sessionId}:${configVersion ?? 'null'}`;
}

/**
 * Fetch with dedup: if the same key is already in-flight, return that promise.
 * Cancel stale requests via AbortController.
 * Single retry on network failure.
 *
 * @param {string} key - cache key
 * @param {Function} fetchFn - () => Promise<Response>
 * @param {object} opts - { signal?: AbortSignal }
 */
export async function dedupFetch(key, fetchFn, opts = {}) {
  // Return cached if available
  const cached = _cache.get(key);
  if (cached) return cached;

  // Dedup in-flight
  if (_inflight.has(key)) return _inflight.get(key);

  const promise = (async () => {
    let lastError;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const result = await fetchFn(opts.signal);
        _cache.set(key, result);
        return result;
      } catch (err) {
        lastError = err;
        if (err.name === 'AbortError' || err.name === 'CanceledError') throw err;
        if (attempt === 0) continue; // retry once
      }
    }
    throw lastError;
  })();

  _inflight.set(key, promise);
  try {
    return await promise;
  } finally {
    _inflight.delete(key);
  }
}

/**
 * Invalidate cache for a session.
 */
export function invalidateCache(sessionId) {
  for (const key of _cache.keys()) {
    if (key.startsWith(`${sessionId}:`)) {
      _cache.delete(key);
    }
  }
}

/**
 * Clear all cache.
 */
export function clearCache() {
  _cache.clear();
}
